import { useCallback, useEffect, useState } from 'react'
import { collection, doc, getDoc, getDocs } from 'firebase/firestore/lite'
import { db } from '../lib/firebase'

/**
 * Lectura del catálogo completo.
 *
 * Es una lectura puntual, no una suscripción: son 5-15 productos que cambian
 * rara vez, y evitar el motor de tiempo real recorta el bundle de forma
 * notable. Un cambio en Firestore se refleja al recargar la página (o, en el
 * panel admin, al llamar `recargar()` tras crear/editar/borrar).
 *
 * El orden se resuelve en cliente —destacados primero, luego alfabético— para
 * no tener que mantener un índice compuesto en Firestore.
 */
export function useProductos() {
  const [productos, setProductos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)

  const recargar = useCallback(() => {
    let vigente = true
    setCargando(true)
    setError(null)

    getDocs(collection(db, 'productos'))
      .then((snapshot) => {
        if (!vigente) return

        const items = snapshot.docs
          .map((doc) => ({ ...doc.data(), id: doc.id }))
          .sort((a, b) => {
            if (a.destacado !== b.destacado) return a.destacado ? -1 : 1
            return (a.nombre ?? '').localeCompare(b.nombre ?? '', 'es')
          })

        setProductos(items)
        setCargando(false)
      })
      .catch((err) => {
        if (!vigente) return
        console.error('useProductos:', err)
        setError('No pudimos cargar el catálogo. Intenta de nuevo en un momento.')
        setCargando(false)
      })

    return () => {
      vigente = false
    }
  }, [])

  useEffect(() => recargar(), [recargar])

  return { productos, cargando, error, recargar }
}

/**
 * Lectura de un producto puntual para la página de detalle. Separado de
 * useProductos porque abrir /producto/:id no debería pagar el costo de
 * traer todo el catálogo primero.
 */
export function useProducto(id) {
  const [producto, setProducto] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let vigente = true
    setCargando(true)
    setError(null)
    setProducto(null)

    getDoc(doc(db, 'productos', id))
      .then((snap) => {
        if (!vigente) return
        setProducto(snap.exists() ? { ...snap.data(), id: snap.id } : null)
        setCargando(false)
      })
      .catch((err) => {
        if (!vigente) return
        console.error('useProducto:', err)
        setError('No pudimos cargar este producto. Intenta de nuevo en un momento.')
        setCargando(false)
      })

    return () => {
      vigente = false
    }
  }, [id])

  return { producto, cargando, error }
}
